import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useLoadData from "@/hooks/useLoadData";
import TwoColumnTable from "@/charts/TwoColumnTable";
import FourColumnTable from "@/charts/FourColumnTable";
import NColumnTable from "@/charts/NColumnTable";
import { showApiError, showSuccess } from "@/utils";
import PasswordInput from "@/components/ui/PasswordInput";

const UserModal = ({
  userFormData,
  handleUserInputChange,
  isUserSubmitting,
  setShowAddUserModal,
  resetUserForm,
  handleUserSubmit,
  allClients,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {t("superAdmin.users.addNewUser", "Add New User")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowAddUserModal(false);
                resetUserForm();
              }}
              disabled={isUserSubmitting}
            ></button>
          </div>
          <form onSubmit={handleUserSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-email" className="form-label">
                      {t("superAdmin.users.email", "Email")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="user-email"
                      name="email"
                      value={userFormData.email}
                      onChange={handleUserInputChange}
                      required
                      disabled={isUserSubmitting}
                      placeholder="Enter email"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-password" className="form-label">
                      {t("superAdmin.users.password", "Password")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <PasswordInput
                      id="user-password"
                      name="password"
                      value={userFormData.password}
                      onChange={handleUserInputChange}
                      required
                      disabled={isUserSubmitting}
                      placeholder="Enter password"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-firstname" className="form-label">
                      {t("superAdmin.users.firstname", "First Name")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="user-firstname"
                      name="firstname"
                      value={userFormData.firstname}
                      onChange={handleUserInputChange}
                      required
                      disabled={isUserSubmitting}
                      placeholder="Enter first name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-lastname" className="form-label">
                      {t("superAdmin.users.lastname", "Last Name")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="user-lastname"
                      name="lastname"
                      value={userFormData.lastname}
                      onChange={handleUserInputChange}
                      required
                      disabled={isUserSubmitting}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-client" className="form-label">
                      {t("superAdmin.users.client", "Client")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      id="user-client"
                      name="client_id"
                      value={userFormData.client_id}
                      onChange={handleUserInputChange}
                      required
                      disabled={isUserSubmitting}
                    >
                      <option value="">Select a client...</option>
                      {allClients?.data?.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.client_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label
                      htmlFor="user-profile-picture"
                      className="form-label"
                    >
                      {t(
                        "superAdmin.users.profilePictureUrl",
                        "Profile Picture URL",
                      )}
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="user-profile-picture"
                      name="profile_picture"
                      value={userFormData.profile_picture}
                      onChange={handleUserInputChange}
                      disabled={isUserSubmitting}
                      placeholder="Enter profile picture URL"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddUserModal(false);
                  resetUserForm();
                }}
                disabled={isUserSubmitting}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isUserSubmitting ||
                  !userFormData.email.trim() ||
                  !userFormData.password.trim() ||
                  !userFormData.firstname.trim() ||
                  !userFormData.lastname.trim() ||
                  !userFormData.client_id
                }
                style={{
                  backgroundColor: "var(--btn-solid-bg)",
                  color: "var(--btn-solid-text)",
                }}
              >
                {isUserSubmitting
                  ? t("common.adding", "Creating...")
                  : t("superAdmin.users.createUser", "Create User")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

function SuperAdminUser() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [allClients, setAllClients] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isUserSubmitting, setIsUserSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState("Welcome to elelem");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [lastSentRecipient, setLastSentRecipient] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState("Copy");
  const [userFormData, setUserFormData] = useState({
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    client_id: "",
    profile_picture: "",
  });

  // Fetch all clients on component mount
  useEffect(() => {
    const fetchAllClients = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/client/all`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          const clientsData = await response.json();
          setAllClients(clientsData);
        } else {
          console.error(
            "Failed to fetch clients:",
            response.status,
            response.statusText,
          );
          showApiError(
            `Failed to fetch clients: ${response.status} ${response.statusText}`,
          );
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        showApiError("Failed to fetch clients");
      }
    };

    fetchAllClients();
  }, [BACKEND_URL]);

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetUserForm = () => {
    setUserFormData({
      email: "",
      password: "",
      firstname: "",
      lastname: "",
      client_id: "",
      profile_picture: "",
    });
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setIsUserSubmitting(true);

    try {
      const userData = {
        email: userFormData.email.trim(),
        password: userFormData.password,
        firstname: userFormData.firstname.trim(),
        lastname: userFormData.lastname.trim(),
        client_id: userFormData.client_id,
        profile_picture: userFormData.profile_picture.trim() || null,
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(userData),
      });

      const result = await response.json();

      if (response.ok) {
        setShowAddUserModal(false);
        resetUserForm();

        showSuccess(result.message || "User created successfully!");
        setSuccessMessage(result.message || "User created successfully!");
        setEmailRecipient(userData.email);
        setEmailSubject("Welcome to elelem");
        setEmailMessage(result.email_body || "");
        setEmailSentSuccess(false);
        setLastSentRecipient("");
        setShowEmailComposer(true);
        setShowSuccessNotification(true);

        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 5000);
      } else {
        showApiError(
          result.message || result.detail || "Failed to create user",
        );
      }
    } catch (error) {
      console.error("Error creating user:", error);
      showApiError("An error occurred while creating the user");
    } finally {
      setIsUserSubmitting(false);
    }
  };

  const handleRecipientChange = (e) => {
    setEmailRecipient(e.target.value);
    setEmailSentSuccess(false);
  };

  const handleSubjectChange = (e) => {
    setEmailSubject(e.target.value);
    setEmailSentSuccess(false);
  };

  const handleBodyChange = (e) => {
    setEmailMessage(e.target.value);
    setEmailSentSuccess(false);
  };

  const handleSendEmail = async () => {
    if (!emailRecipient.trim() || !emailMessage.trim()) {
      showApiError("Recipient email and body cannot be empty");
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/user/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          to_email: emailRecipient.trim(),
          subject: emailSubject.trim() || "Welcome to elelem",
          body: emailMessage,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess(result.message || "Email sent successfully!");
        setEmailSentSuccess(true);
        setLastSentRecipient(emailRecipient.trim());
      } else {
        showApiError(
          result.message || result.detail || "Failed to send email",
        );
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showApiError("An error occurred while sending the email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleCopyEmailBody = async () => {
    try {
      await navigator.clipboard.writeText(emailMessage);
      setCopyButtonText("Copied!");
      setTimeout(() => {
        setCopyButtonText("Copy");
      }, 2000);
    } catch (error) {
      console.error("Failed to copy text:", error);
      const textArea = document.createElement("textarea");
      textArea.value = emailMessage;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopyButtonText("Copied!");
        setTimeout(() => {
          setCopyButtonText("Copy");
        }, 2000);
      } catch (fallbackError) {
        console.error("Fallback copy failed:", fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  const [ocSearchTerm, setOcSearchTerm] = useState("");

  const [loginActivity, loginActivityLoaded, loginActivityError] = useLoadData(
    `${BACKEND_URL}/api/v1/user-activity/login`,
  );

  const [
    optimizeContentActivity,
    optimizeContentActivityLoaded,
    optimizeContentActivityError,
  ] = useLoadData(`${BACKEND_URL}/api/v1/user-activity/optimize-content`);

  // Filter login activity data based on search term
  const filteredLoginActivity = useMemo(() => {
    if (!loginActivity?.user_login_summary || !userSearchTerm.trim()) {
      return loginActivity?.user_login_summary || [];
    }

    const searchLower = userSearchTerm.toLowerCase().trim();

    return loginActivity.user_login_summary.filter((user) => {
      const email = user.username?.toLowerCase() || "";
      const clientId = user.client_id?.toLowerCase() || "";

      return email.includes(searchLower) || clientId.includes(searchLower);
    });
  }, [loginActivity, userSearchTerm]);

  // Clear search function
  const clearSearch = () => {
    setUserSearchTerm("");
  };

  // Filter optimize content activity data based on search term
  const filteredOcActivity = useMemo(() => {
    if (!optimizeContentActivity?.activity_summary || !ocSearchTerm.trim()) {
      return optimizeContentActivity?.activity_summary || [];
    }

    const searchLower = ocSearchTerm.toLowerCase().trim();

    return optimizeContentActivity.activity_summary.filter((row) => {
      const email = row.username?.toLowerCase() || "";
      const clientId = row.client_id?.toLowerCase() || "";
      const action = row.action?.toLowerCase() || "";

      return (
        email.includes(searchLower) ||
        clientId.includes(searchLower) ||
        action.includes(searchLower)
      );
    });
  }, [optimizeContentActivity, ocSearchTerm]);

  const convertedOcActivity = useMemo(() => {
    return filteredOcActivity.map((row) => ({
      ...row,
      response_time_s:
        row.avg_response_time_ms != null
          ? (row.avg_response_time_ms / 1000).toFixed(2)
          : "-",
    }));
  }, [filteredOcActivity]);

  return (
    <div>
      <h4 className="h5 fw-bold text-dark mb-2">
        {t("superAdmin.users.title", "User Management")}
      </h4>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.users.subtitle",
          "Recent user activity and create new user accounts",
        )}
      </small>

      <div className="mb-3 d-flex align-items-center gap-3">
        <div className="position-relative" style={{ maxWidth: 500, flex: 1 }}>
          <input
            type="text"
            className="form-control"
            placeholder={t(
              "superAdmin.users.searchByEmailOrClientId",
              "Search by email or client ID...",
            )}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
          />
          {userSearchTerm && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
              onClick={clearSearch}
              style={{
                border: "none",
                background: "none",
                color: "#6c757d",
                padding: "2px 4px",
              }}
              title="Clear search"
            >
              <i className="bi bi-x-circle" aria-hidden="true"></i>
            </button>
          )}
        </div>
        <div className="d-flex flex-column align-items-start">
          <button
            className="btn btn-light"
            style={{
              color: "var(--brand-color-dark)",
              borderColor: "var(--brand-color-dark)",
            }}
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="bi bi-plus-circle"> </i>{" "}
            {t("superAdmin.users.addUser", "User")}
          </button>
        </div>
      </div>

      {showSuccessNotification && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          <i className="bi bi-check-circle-fill me-2"></i>
          <strong>Success!</strong> {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowSuccessNotification(false)}
          ></button>
        </div>
      )}

      {showEmailComposer && (
        <div className="card card-radius mb-4 shadow-sm">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="bi bi-envelope-fill me-2"></i>
              Welcome Email Composer
            </h5>
            {emailSentSuccess && lastSentRecipient && (
              <span className="badge bg-success">
                <i className="bi bi-check-circle me-1"></i> Sent to {lastSentRecipient}
              </span>
            )}
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="email-recipient" className="form-label fw-bold">
                To:
              </label>
              <input
                type="email"
                id="email-recipient"
                className="form-control"
                value={emailRecipient}
                onChange={handleRecipientChange}
                disabled={isSendingEmail}
                placeholder="recipient@example.com"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="email-subject" className="form-label fw-bold">
                Subject:
              </label>
              <input
                type="text"
                id="email-subject"
                className="form-control"
                value={emailSubject}
                onChange={handleSubjectChange}
                disabled={isSendingEmail}
                placeholder="Email Subject"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="email-body" className="form-label fw-bold">
                Message Body:
              </label>
              <textarea
                id="email-body"
                className="form-control"
                rows={10}
                value={emailMessage}
                onChange={handleBodyChange}
                disabled={isSendingEmail}
                style={{ fontSize: "0.9rem", lineHeight: "1.5" }}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={handleSendEmail}
                disabled={
                  isSendingEmail ||
                  !emailRecipient.trim() ||
                  !emailMessage.trim()
                }
                style={{
                  backgroundColor: "var(--brand-color-dark)",
                  borderColor: "var(--brand-color-dark)",
                }}
              >
                <i className="bi bi-send me-1"></i>
                {isSendingEmail ? "Sending Email..." : "Send Email"}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleCopyEmailBody}
                disabled={isSendingEmail}
              >
                <i className="bi bi-clipboard me-1"></i>
                {copyButtonText}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={() => {
                  setShowEmailComposer(false);
                  setEmailSentSuccess(false);
                }}
                disabled={isSendingEmail}
              >
                <i className="bi bi-x-circle me-1"></i>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

       <h5 className="card-title">
        {t("superAdmin.users.loginActivity", "Recent Login Activity")}
      </h5>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.users.loginActivitySubtitle",
          "User login activity in the last 7 days",
        )}
      </small>
      <div className="row">
        <div className="col-lg-12">
          <FourColumnTable
            data={filteredLoginActivity}
            column1Key={"username"}
            column1Title={t("superAdmin.users.email", "Email")}
            column2Key={"client_id"}
            column2Title={t("superAdmin.users.clientId", "Client ID")}
            column3Key={"last_login"}
            column3Title={t("superAdmin.users.lastLogin", "Last Login")}
            column4Key={"total_number_of_logins"}
            column4Title={t("superAdmin.users.totalLogins", "Total Logins")}
            isLoaded={loginActivityLoaded}
            error={loginActivityError}
          />
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-md-6">
          <TwoColumnTable
            data={loginActivity?.browser_usage || []}
            column1Key={"browser_name"}
            column1Title={t("superAdmin.users.browser", "Browser")}
            column2Key={"count"}
            column2Title={t("superAdmin.users.count", "Count")}
            isLoaded={loginActivityLoaded}
            error={loginActivityError}
          />
        </div>
        <div className="col-md-6">
          <TwoColumnTable
            data={loginActivity?.device_type_usage || []}
            column1Key={"device_type"}
            column1Title={t("superAdmin.users.device", "Device")}
            column2Key={"count"}
            column2Title={t("superAdmin.users.count", "Count")}
            isLoaded={loginActivityLoaded}
            error={loginActivityError}
          />
        </div>
      </div>

      <br />
      <h5 className="card-title">
        {t(
          "superAdmin.users.optimizeContentActivity",
          "Optimize Content Activity",
        )}
      </h5>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.users.optimizeContentActivitySubtitle",
          "Aggregated results of Optimize Content activity in the last 7 days",
        )}
      </small>
      <div className="row">
        <div className="col-md-4">
          <TwoColumnTable
            data={optimizeContentActivity?.activity_type_breakdown || []}
            column1Key={"action"}
            column1Title={t("superAdmin.users.action", "Action")}
            column2Key={"total_count"}
            column2Title={t("superAdmin.users.count", "Count")}
            columnRatio={"80-20"}
            isLoaded={optimizeContentActivityLoaded}
            error={optimizeContentActivityError}
            boxHeight="220px"
          />
        </div>
      </div>
      <br />
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.users.optimizeContentIndividualSubtitle",
          "Optimize content usage of individual users in the last 7 days",
        )}
      </small>
      <div className="mb-3 position-relative" style={{ maxWidth: 500 }}>
        <input
          type="text"
          className="form-control"
          placeholder={t(
            "superAdmin.users.searchByEmailClientOrAction",
            "Search by email, client ID, or action...",
          )}
          aria-label="Search optimize content activity by email, client ID, or action"
          value={ocSearchTerm}
          onChange={(e) => setOcSearchTerm(e.target.value)}
        />
        {ocSearchTerm && (
          <button
            type="button"
            className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
            onClick={() => setOcSearchTerm("")}
            style={{
              border: "none",
              background: "none",
              color: "#6c757d",
              padding: "2px 4px",
            }}
            title="Clear search"
            aria-label="Clear search"
          >
            <i className="bi bi-x-circle" aria-hidden="true"></i>
          </button>
        )}
      </div>
      <div className="row">
        <div className="col-md-12">
          <NColumnTable
            data={convertedOcActivity}
            n={6}
            columnKeys={[
              "username",
              "client_id",
              "action",
              "total_count",
              "last_used",
              "response_time_s",
            ]}
            columnTitles={[
              t("superAdmin.users.email", "Email"),
              t("superAdmin.users.clientId", "Client ID"),
              t("superAdmin.users.action", "Action"),
              t("superAdmin.users.count", "Count"),
              t("superAdmin.users.lastUsed", "Last Used"),
              t("superAdmin.users.avgResponseTime", "Avg Response Time (s)"),
            ]}
            columnRatio="20-20-20-10-20-10"
            columnAlignment="l-l-l-l-l-l"
            isLoaded={optimizeContentActivityLoaded}
            error={optimizeContentActivityError}
          />
        </div>
      </div>

      {showAddUserModal && (
        <UserModal
          userFormData={userFormData}
          handleUserInputChange={handleUserInputChange}
          isUserSubmitting={isUserSubmitting}
          setShowAddUserModal={setShowAddUserModal}
          resetUserForm={resetUserForm}
          handleUserSubmit={handleUserSubmit}
          allClients={allClients}
        />
      )}
    </div>
  );
}

export default SuperAdminUser;
